import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, LogOut, Trash2, Phone, Mail, Car, RefreshCw, Inbox, ExternalLink,
} from "lucide-react";
import { adminApi, clearToken, getToken } from "@/lib/auth";
import { LOGO } from "@/data/site";

const STATUSES = ["new", "contacted", "scheduled", "closed"];
const STATUS_STYLE = {
  new: "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-gold",
  contacted: "border-blue-400/40 bg-blue-400/10 text-blue-300",
  scheduled: "border-green-400/40 bg-green-400/10 text-green-300",
  closed: "border-white/15 bg-white/5 text-neutral-400",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/bookings");
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        nav("/admin/login");
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await adminApi.patch(`/bookings/${id}`, { status });
      setBookings((b) => b.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/bookings/${id}`);
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Quote deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    clearToken();
    nav("/admin/login");
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: bookings.filter((b) => b.status === s).length }), {});

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-black/80 backdrop-blur-xl z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="AS Auto Works" className="h-11 w-auto object-contain" />
            <span className="hidden sm:block text-xs tracking-[0.25em] uppercase text-neutral-500 font-mono-x">Lead Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-gold transition-colors px-3 py-2">
              <RefreshCw size={16} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button data-testid="admin-view-site" onClick={() => window.open("/", "_blank")} className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-gold transition-colors px-3 py-2">
              <ExternalLink size={16} /> <span className="hidden sm:inline">View Site</span>
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 bg-transparent border border-white/20 text-white text-sm px-4 py-2 hover:bg-[#CC1100] hover:border-[#CC1100] transition-colors uppercase tracking-widest">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
          <div>
            <h1 className="font-heading font-bold uppercase tracking-tight text-3xl sm:text-4xl">
              Incoming <span className="gold-gradient-text">Quotes</span>
            </h1>
            <p className="text-neutral-500 mt-2 text-sm">{bookings.length} total request{bookings.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterPill label="All" value="all" active={filter} count={bookings.length} onClick={setFilter} />
            {STATUSES.map((s) => (
              <FilterPill key={s} label={s} value={s} active={filter} count={counts[s]} onClick={setFilter} />
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-neutral-500">
            <Loader2 className="animate-spin mr-3" /> Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-empty" className="flex flex-col items-center justify-center py-32 text-neutral-600 border border-dashed border-white/10">
            <Inbox size={48} strokeWidth={1} />
            <p className="mt-4 uppercase tracking-widest text-sm">No quotes here yet</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-bookings-list">
            {filtered.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-[#0A0A0A] border border-white/10 p-5 md:p-6 hover:border-white/20 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-semibold text-xl">{b.name}</h3>
                      <span className={`text-[10px] font-mono-x uppercase tracking-wider px-2.5 py-1 border ${STATUS_STYLE[b.status] || ""}`}>{b.status}</span>
                      <span className="text-xs text-neutral-600">{new Date(b.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-neutral-300">
                      <a href={`mailto:${b.email}`} className="inline-flex items-center gap-2 hover:text-gold"><Mail size={14} /> {b.email}</a>
                      {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-2 hover:text-gold"><Phone size={14} /> {b.phone}</a>}
                      <span className="inline-flex items-center gap-2"><Car size={14} className="text-gold" /> {b.vehicle}</span>
                    </div>
                    <p className="mt-2 text-sm text-gold/90 font-medium">{b.service}</p>
                    {b.message && <p className="mt-2 text-sm text-neutral-400 italic">"{b.message}"</p>}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      data-testid={`status-select-${b.id}`}
                      value={b.status}
                      onChange={(e) => updateStatus(b.id, e.target.value)}
                      className="bg-[#111] border border-white/15 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-[#D4AF37] cursor-pointer"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#111]">{s}</option>
                      ))}
                    </select>
                    <button
                      data-testid={`delete-${b.id}`}
                      onClick={() => remove(b.id)}
                      className="w-10 h-10 flex items-center justify-center border border-white/15 text-neutral-400 hover:text-white hover:bg-[#CC1100] hover:border-[#CC1100] transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const FilterPill = ({ label, value, active, count, onClick }) => (
  <button
    data-testid={`filter-${value}`}
    onClick={() => onClick(value)}
    className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
      active === value ? "bg-gold text-black border-[#D4AF37]" : "border-white/15 text-neutral-400 hover:text-white"
    }`}
  >
    {label} {typeof count === "number" ? `(${count})` : ""}
  </button>
);
